import { NOTE_TAG_MAX, NOTE_TAGS_MAX, NOTE_TITLE_MAX } from '@pm/schemas';
import { Button, ErrorState, Field, Input, Kbd, MarkdownEditor, Skeleton, TagInput } from '@pm/ui';
import { ArrowLeft, Lock } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { useNote, useNoteTags, useSaveNote } from '@/lib/notes';
import { useToast } from '@/lib/toast';
import { useBackToNotes } from './use-back-to-notes';

interface Draft {
  title: string;
  content: string;
  tags: string[];
}
const EMPTY: Draft = { title: '', content: '', tags: [] };
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
const same = (a: Draft, b: Draft) =>
  a.title === b.title && a.content === b.content && a.tags.join('\n') === b.tags.join('\n');

/** Nova anotação (/anotacoes/nova) ou edição (/anotacoes/:id/editar). */
export function NoteEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { data: note, isLoading, isError, error, refetch } = useNote(id);

  if (id && isLoading) {
    return (
      <div className="flex max-w-[960px] flex-col gap-3">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-control w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }
  if (id && (isError || !note)) {
    return <ErrorState message={error?.message} onRetry={() => void refetch()} />;
  }
  const initial = note
    ? { title: note.title, content: note.content_markdown, tags: note.tags }
    : EMPTY;
  return <Editor key={note?.id ?? 'nova'} id={id} initial={initial} />;
}

function Editor({ id, initial }: { id?: string; initial: Draft }) {
  const navigate = useNavigate();
  const toast = useToast();
  const back = useBackToNotes();
  const save = useSaveNote();
  const tagSuggestions = useNoteTags().data?.map((t) => t.tag) ?? [];
  const [draft, setDraft] = useState<Draft>(initial);
  const [titleError, setTitleError] = useState<string>();
  const leaving = useRef(false);

  const dirty = useMemo(() => !same(draft, initial), [draft, initial]);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !leaving.current && currentLocation.pathname !== nextLocation.pathname,
  );

  // Fechar ou recarregar a aba com alterações: aviso do navegador.
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const submit = () => {
    const title = draft.title.trim();
    if (!title) {
      setTitleError('Dê um título à anotação.');
      return;
    }
    if (save.isPending) return;
    save.mutate(
      { id, body: { title, content_markdown: draft.content, tags: draft.tags } },
      {
        onSuccess: (saved) => {
          leaving.current = true;
          toast.success(id ? 'Anotação salva' : 'Anotação criada');
          void navigate(`/anotacoes/${saved.id}`, { replace: true });
        },
        onError: (err) => toast.error(err),
      },
    );
  };

  // Cmd/Ctrl+S salva de qualquer campo.
  const submitRef = useRef(submit);
  useEffect(() => {
    submitRef.current = submit;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        submitRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const cancel = () => (id ? void navigate(`/anotacoes/${id}`) : back());

  return (
    <div className="flex max-w-[960px] flex-col gap-gap-lg">
      <div className="flex items-center gap-2 font-mono text-xs text-text-3">
        <Button variant="secondary" size="sm" className="mr-1 font-sans" onClick={cancel}>
          <ArrowLeft /> Voltar
        </Button>
        <Lock className="size-3" />
        <span>{id ? 'Editando anotação' : 'Nova anotação'}</span>
      </div>

      <form
        className="flex flex-col gap-gap"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="Título" htmlFor="note-title" error={titleError}>
          <Input
            id="note-title"
            value={draft.title}
            maxLength={NOTE_TITLE_MAX}
            autoFocus={!id}
            invalid={Boolean(titleError)}
            placeholder="Ex.: Ideias para o planejamento de 2027"
            onChange={(e) => {
              setDraft((d) => ({ ...d, title: e.target.value }));
              if (titleError) setTitleError(undefined);
            }}
          />
        </Field>

        <Field
          label="Tags"
          htmlFor="note-tags"
          hint="Enter ou vírgula para adicionar. Use as setas para escolher uma tag que você já usa."
        >
          <TagInput
            id="note-tags"
            value={draft.tags}
            onChange={(tags) => setDraft((d) => ({ ...d, tags }))}
            suggestions={tagSuggestions}
            max={NOTE_TAGS_MAX}
            maxLength={NOTE_TAG_MAX}
          />
        </Field>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text-2">Conteúdo</span>
          <MarkdownEditor
            value={draft.content}
            onChange={(content) => setDraft((d) => ({ ...d, content }))}
            placeholder={
              '# Título\n\n- listas, **negrito**, [links](https://…), tabelas e `código`'
            }
            minRows={18}
            autoFocus={Boolean(id)}
          />
        </div>

        <div className="flex items-center gap-gap">
          <span className="hidden items-center gap-1 text-xs text-text-3 sm:inline-flex">
            <Kbd>{IS_MAC ? '⌘ S' : 'Ctrl S'}</Kbd> salva
          </span>
          <Button variant="ghost" className="ml-auto" onClick={cancel}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {id ? 'Salvar' : 'Criar anotação'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(open) => {
          if (!open && blocker.state === 'blocked') blocker.reset();
        }}
        title="Descartar alterações?"
        description="O que você escreveu desde o último salvamento será perdido."
        confirmLabel="Descartar"
        danger
        onConfirm={() => blocker.state === 'blocked' && blocker.proceed()}
      />
    </div>
  );
}
